import { useEffect } from 'react';
import { createFileRoute, redirect, Outlet, useLocation } from '@tanstack/react-router';
import { AppSidebar, PhoneNotice, TopHeader } from '@/components/navigation';
import {
  BillingBanner,
  CampaignGate,
  DemoBanner,
  ImportBanner,
  NoAccessBanner,
  PaywallOverlay,
} from '@/components/campaign';
import { CalendarSyncBanner } from '@/components/calendar';
import { useCampaignDeepLink, useMayManageCampaign } from '@/hooks';
import { useImportProgress } from '@/hooks/queries';
import { useImportProgressStore } from '@/stores/importProgressStore';
import { isPaywallExemptPath } from '@/lib/paywallExemptRoutes';

export const Route = createFileRoute('/app')({
  beforeLoad: ({ context, location }) => {
    if (!context.auth.user) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
  component: AppLayout,
});

function AppLayout() {
  // SSE import progress → Zustand store sync
  const sseGeneration = useImportProgressStore((s) => s.sseGeneration);
  const setProgress = useImportProgressStore((s) => s.setProgress);
  const mayImport = useMayManageCampaign();
  const { progress } = useImportProgress(sseGeneration, mayImport);
  useEffect(() => { setProgress(progress); }, [progress, setProgress]);

  // Email deep-link auto-switch: `?campaignId=<guid>` → select that campaign
  useCampaignDeepLink();

  return (
    <CampaignGate>
      <div className="h-screen flex flex-col bg-gray-50">
        {/* Top header with logo, campaign selector, user menu */}
        <TopHeader />
        <DemoBanner />
        <NoAccessBanner />
        <BillingBanner />
        <ImportBanner />
        <CalendarSyncBanner />
        <PhoneNotice />

        {/* Main content area with sidebar */}
        <div className="flex flex-1 overflow-hidden">
          <AppSidebar />
          <AppContent />
        </div>
      </div>
    </CampaignGate>
  );
}

function AppContent() {
  const { pathname } = useLocation();
  const isFree = isPaywallExemptPath(pathname);

  return (
    <main className="flex-1 overflow-auto p-6">
      {isFree ? <Outlet /> : <PaywallOverlay><Outlet /></PaywallOverlay>}
    </main>
  );
}
